import type {
  ButtonBlock,
  EmailBlock,
  EmailDocument,
  EmailTheme,
  FooterBlock,
  HeadingBlock,
  ImageBlock,
  ListBlock,
  LogoBlock,
  SpacerBlock,
  TextBlock,
} from '../model/types';

/**
 * Zamiana dokumentu na HTML wiadomości.
 *
 * DLACZEGO TABELE I STYLE W ATRYBUCIE `style`, a nie zwykły layout z arkuszem stylów:
 * Outlook na Windows renderuje pocztę silnikiem Worda, który nie zna flexboksa, grida ani
 * pozycjonowania, a Gmail wycina znaczniki `<style>` z treści wiadomości przy niektórych
 * ścieżkach (np. przekazywanie dalej). Tabela z wierszami i style wpisane przy elemencie
 * to jedyny układ, który wygląda tak samo w każdej skrzynce.
 *
 * Funkcja jest czysta: ten sam dokument zawsze daje ten sam HTML. Dzięki temu podgląd w
 * kreatorze i wiadomość wysłana do użytkownika to dokładnie ten sam kod.
 */

/** Zamiana znaków o znaczeniu w HTML. Pola formularza są tekstem, nie kodem. */
const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/** Adres w atrybucie. Odcinamy schematy wykonujące kod, np. `javascript:`. */
const safeUrl = (value: string): string => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  const allowed = lower.startsWith('http://')
    || lower.startsWith('https://')
    || lower.startsWith('mailto:')
    || lower.startsWith('tel:')
    || lower.startsWith('{{');
  return allowed ? escapeHtml(trimmed) : '';
};

/** Akapity z pustych linii i pojedyncze złamania wiersza, jak w polu tekstowym. */
const paragraphs = (value: string): string[] => value
  .split(/\n{2,}/)
  .map((part) => part.trim())
  .filter(Boolean);

const inlineText = (value: string): string => escapeHtml(value).replace(/\n/g, '<br />');

const cellPadding = '0 32px';

const row = (content: string): string => `<tr><td style="padding:${cellPadding};">${content}</td></tr>`;

const logo = (block: LogoBlock): string => {
  const src = safeUrl(block.src);
  if (!src) return '';
  return row(
    `<div style="text-align:${block.align};padding:0 0 20px;">`
    + `<img src="${src}" alt="${escapeHtml(block.alt)}" height="${block.height}"`
    + ` style="height:${block.height}px;width:auto;border:0;display:inline-block;" border="0" /></div>`,
  );
};

const heading = (block: HeadingBlock, theme: EmailTheme): string => {
  const size = block.level === 1 ? 28 : 20;
  const margin = block.level === 1 ? '0 0 12px' : '0 0 8px';
  return row(
    `<h${block.level} class="hs-h${block.level}" style="margin:${margin};font-family:${theme.fontFamily};`
    + `font-size:${size}px;line-height:1.25;font-weight:600;color:${theme.text};text-align:${block.align};">`
    + `${inlineText(block.text)}</h${block.level}>`,
  );
};

const text = (block: TextBlock, theme: EmailTheme): string => {
  const size = block.small ? 13 : 15;
  const color = block.small ? theme.muted : theme.text;
  const body = paragraphs(block.text)
    .map((part) => `<p style="margin:0 0 12px;font-family:${theme.fontFamily};font-size:${size}px;`
      + `line-height:1.6;color:${color};text-align:${block.align};">${inlineText(part)}</p>`)
    .join('');
  return row(body);
};

const button = (block: ButtonBlock, theme: EmailTheme): string => {
  const href = safeUrl(block.href) || '#';
  const width = block.fullWidth ? 'width:100%;' : '';
  const { align } = block;
  /* Przycisk jako tabela, nie sam odsyłacz: Outlook gubi `padding` na elementach
     liniowych, więc tło skurczyłoby się do samego napisu. */
  /* Przycisk jest CELOWO NIEWIELKI: w wiadomości do administratora ma być czytelnym
     odnośnikiem, a nie banerem na pół szerokości. */
  const radius = 10;
  return row(
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="${width}margin:4px 0 8px;"`
    + ` align="${align}"><tr><td align="center" bgcolor="${theme.brand}"`
    + ` style="border-radius:${radius}px;background:${theme.brand};">`
    + `<a href="${href}" style="display:inline-block;padding:9px 18px;font-family:${theme.fontFamily};`
    + `font-size:13px;font-weight:600;color:#ffffff;text-decoration:none;border-radius:${radius}px;">`
    + `${escapeHtml(block.label)}</a></td></tr></table>`,
  );
};

const image = (block: ImageBlock, theme: EmailTheme): string => {
  const src = safeUrl(block.src);
  if (!src) return '';
  const img = `<img src="${src}" alt="${escapeHtml(block.alt)}" width="${theme.width - 64}"`
    + ' style="display:block;width:100%;max-width:100%;height:auto;border:0;outline:none;text-decoration:none;"'
    + ' border="0" />';
  const href = safeUrl(block.href);
  const content = href ? `<a href="${href}" style="text-decoration:none;">${img}</a>` : img;
  return row(`<div style="margin:4px 0 16px;border-radius:${block.radius}px;overflow:hidden;">${content}</div>`);
};

const list = (block: ListBlock, theme: EmailTheme): string => {
  const items = block.items
    .filter((item) => item.trim())
    .map((item) => `<li style="margin:0 0 8px;font-family:${theme.fontFamily};font-size:15px;`
      + `line-height:1.6;color:${theme.text};">${inlineText(item)}</li>`)
    .join('');
  if (!items) return '';
  return row(`<ul style="margin:0 0 12px;padding:0 0 0 20px;">${items}</ul>`);
};

const divider = (theme: EmailTheme): string => row(
  `<div style="height:1px;line-height:1px;font-size:0;background:${theme.border};margin:12px 0;">&nbsp;</div>`,
);

const spacer = (block: SpacerBlock): string => `<tr><td style="height:${block.height}px;`
  + `line-height:${block.height}px;font-size:0;">&nbsp;</td></tr>`;

const footer = (block: FooterBlock, theme: EmailTheme): string => {
  const unsubscribe = safeUrl(block.unsubscribeUrl);
  const link = unsubscribe
    ? `<br /><a href="${unsubscribe}" style="color:${theme.muted};text-decoration:underline;">`
      + `${escapeHtml(block.unsubscribeLabel)}</a>`
    : '';
  return row(
    `<p style="margin:16px 0 0;font-family:${theme.fontFamily};font-size:11px;line-height:1.5;`
    + `color:${theme.muted};text-align:center;">${inlineText(block.text)}${link}</p>`,
  );
};

const renderBlock = (block: EmailBlock, theme: EmailTheme): string => {
  switch (block.kind) {
    case 'logo': return logo(block);
    case 'heading': return heading(block, theme);
    case 'text': return text(block, theme);
    case 'button': return button(block, theme);
    case 'image': return image(block, theme);
    case 'list': return list(block, theme);
    case 'divider': return divider(theme);
    case 'spacer': return spacer(block);
    case 'footer': return footer(block, theme);
    default: return '';
  }
};

/** Ukryta zajawka. Skrzynki pokazują ją obok tematu, więc nie może zostać pusta. */
const preheader = (value: string, theme: EmailTheme): string => {
  if (!value.trim()) return '';
  return '<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;'
    + `font-size:1px;line-height:1px;color:${theme.background};opacity:0;">`
    + `${escapeHtml(value)}</div>`;
};

export const renderEmailHtml = (doc: EmailDocument): string => {
  const { theme } = doc;
  const body = doc.blocks.map((block) => renderBlock(block, theme)).join('');

  return `<!doctype html>
<html lang="pl">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<meta name="x-apple-disable-message-reformatting" />
<meta name="color-scheme" content="light" />
<meta name="supported-color-schemes" content="light" />
<title>${escapeHtml(doc.subject)}</title>
<style>
  body { margin:0; padding:0; background:${theme.background}; }
  a { color:${theme.brand}; }
  @media only screen and (max-width:600px) {
    .hs-card { width:100% !important; }
    .hs-card td { padding-left:20px !important; padding-right:20px !important; }
    .hs-h1 { font-size:24px !important; }
  }
</style>
</head>
<body style="margin:0;padding:0;background:${theme.background};">
${preheader(doc.preheader, theme)}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
  style="background:${theme.background};">
  <tr>
    <td align="center" style="padding:32px 12px;">
      <!-- Treść bez ramki i cienia: wiadomość ma wyglądać jak list, a nie jak karta
           wyciągnięta z panelu. Strukturę robią nagłówki i cienkie linie. -->
      <table role="presentation" class="hs-card" width="${theme.width}" cellpadding="0" cellspacing="0"
        border="0" style="width:${theme.width}px;max-width:100%;background:${theme.surface};">
        <tr><td style="height:8px;line-height:8px;font-size:0;">&nbsp;</td></tr>
        ${body}
        <tr><td style="height:24px;line-height:24px;font-size:0;">&nbsp;</td></tr>
      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
};
